import { useCallback, useId } from "react";
import { Input } from "@its-engineering-and-development/ui-foundation/components";

export interface ReceiptCollectionRulesProps {
    /** Current "require receipt above" amount (may include a leading "$"). */
    amount: string;
    /** Called when the amount changes. */
    onAmountChange: (value: string) => void;
    /** When true, the amount input is read-only. */
    isDisabled?: boolean;
}

/** Keep only positive numbers: digits with at most one decimal point (no sign). */
function sanitizeAmount(value: string): string {
    const digitsAndDot = value.replace(/[^\d.]/g, "");
    const firstDot = digitsAndDot.indexOf(".");
    if (firstDot === -1) return digitsAndDot;
    return digitsAndDot.slice(0, firstDot + 1) + digitsAndDot.slice(firstDot + 1).replace(/\./g, "");
}

/** Molecule: the "Receipt Collection Rules" section with the required-amount input. */
export function ReceiptCollectionRules({ amount, onAmountChange, isDisabled = false }: ReceiptCollectionRulesProps) {
    const headingId = useId();

    const handleAmountChange = useCallback((value: string) => onAmountChange(sanitizeAmount(value)), [onAmountChange]);

    return (
        <section aria-labelledby={headingId}>
            <div className="mb-3">
                <h3 id={headingId} className="text-base font-semibold text-gray-900">
                    Receipt Collection Rules
                </h3>
                <p className="text-sm text-gray-500">Define when employees must submit receipts.</p>
            </div>

            <div className="flex flex-col gap-5 rounded-lg border border-gray-200 bg-white p-4">
                {/* Require receipt at or above */}
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="text-sm font-semibold text-gray-800">Require receipt at or above</p>
                        <p className="text-sm text-gray-500">Expenses at or above this amount must include a receipt.</p>
                    </div>
                    <div className="relative w-24 shrink-0">
                        <span
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-y-0 left-3 z-10 flex items-center text-sm font-semibold text-gray-700"
                        >
                            $
                        </span>
                        <Input
                            aria-label="Require receipt at or above"
                            inputMode="decimal"
                            inputClassName="pl-7"
                            value={amount.replace(/\$/g, "")}
                            onChange={handleAmountChange}
                            isDisabled={isDisabled}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}
